import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ErrorData {
  title: string;
  message: string;
  showRetryButton?: boolean;
  onRetry?: () => void;
}

interface ErrorContextType {
  error: ErrorData | null;
  showError: (errorData: ErrorData) => void;
  hideError: () => void;
  showAuthError: (customErrorData?: { title: string; message: string }) => void;
  clearStorageAndReload: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [error, setError] = useState<ErrorData | null>(null);

  const showError = (errorData: ErrorData) => {
    setError(errorData);
  };

  const hideError = () => {
    setError(null);
  };

  const clearStorageAndReload = () => {
    // Svuota tutto il localStorage
    localStorage.clear();
    // Ricarica la pagina
    window.location.reload();
  };

  const showAuthError = (customErrorData?: { title: string; message: string }) => {
    const defaultErrorData = {
      title: 'Sessione Scaduta',
      message: 'La tua sessione è scaduta o il token non è più valido. Per favore, effettua nuovamente il login per continuare.'
    };
    
    const errorData = customErrorData || defaultErrorData;
    
    showError({
      title: errorData.title,
      message: errorData.message,
      showRetryButton: true,
      onRetry: clearStorageAndReload
    });
  };

  const value: ErrorContextType = {
    error,
    showError,
    hideError,
    showAuthError,
    clearStorageAndReload
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

// Hook personalizzato per usare il context
export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};
